/**
 * LocaleManager
 * -------------
 * A singleton EventTarget that manages the app’s active language setting.
 * Exposes getters/setters for locale, and dispatches a `locale-changed` event
 * when the locale is updated.
 */

import locales from './locales'; // Importing your locales object

// EventTarget gives us .addEventListener() and .dispatchEvent()
const LocaleManager = new EventTarget();

// Define a sealed API surface via property descriptors
Object.defineProperties(LocaleManager, {
  /**
   * fallbackLocale (getter)
   * -----------------------
   * Returns the first key in `locales` as the ultimate fallback.
   * Non-configurable & non-enumerable by default.
   */
  fallbackLocale: {
    get: function () {
      return this.supportedLocales[0];
    }
  },

  /**
   * supportedLocales (getter)
   * -------------------------
   * Returns an array of all locale codes present in `locales`.
   */
  supportedLocales: {
    get: function () {
      return Object.keys(locales);
    }
  },

  /**
   * activeLocale (getter/setter)
   * ----------------------------
   * Getter:
   *  - Reads `storedLocale` from localStorage if valid,
   *  - Otherwise falls back to browser setting or `fallbackLocale`.
   * Setter:
   *  - Validates against supportedLocales,
   *  - Persists to localStorage,
   *  - Updates <html lang="…">,
   *  - Dispatches `locale-changed` for listeners (e.g. Vuetify, Day.js).
   *
   * Because `configurable: false`, we cannot later redefine or delete this prop.
   */
  activeLocale: {
    get: function () {
      // First, try to get the stored locale from localStorage
      const storedLocale = this.getStoredLocale();
      if (storedLocale) {
        return storedLocale;
      }

      // Fallback to browser's locale or the default locale
      const browserLocale =
        window.navigator.language || window.navigator.userLanguage;
      const localeNoRegionCode = browserLocale.split('-')[0];

      if (this.isLocaleSupported(browserLocale)) {
        return browserLocale;
      }
      if (this.isLocaleSupported(localeNoRegionCode)) {
        return localeNoRegionCode;
      }

      return this.fallbackLocale;
    },
    set: function (newLocale) {
      // Update the locale only if it's supported
      if (!this.isLocaleSupported(newLocale)) return;
      localStorage.setItem('storedLocale', newLocale);
      document.querySelector('html').setAttribute('lang', newLocale);
      this.dispatchEvent(new CustomEvent('locale-changed'));
    }
  },

  /**
   * isLocaleSupported (method)
   * --------------------------
   * Simple lookup to guard setter against invalid codes.
   * Defined as a non-writable, non-configurable function.
   */
  isLocaleSupported: {
    value: function (locale) {
      return this.supportedLocales.includes(locale);
    }
  },

  /**
   * getStoredLocale (method)
   * ------------------------
   * Safely reads from localStorage, returns null on error or if invalid.
   */
  getStoredLocale: {
    value: function () {
      try {
        const storedLocale = localStorage.getItem('storedLocale');
        return this.isLocaleSupported(storedLocale) ? storedLocale : null;
      } catch (error) {
        console.error('Error retrieving stored locale:', error);
        return null;
      }
    }
  }
});

export default LocaleManager;
